import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Unlock, X } from 'lucide-react';

const ADMIN_PASSWORD_LOCAL_STORAGE_KEY = 'kazajah_admin_pwd';

/**
 * InlineAdminGate — small floating button that lets admins authenticate
 * directly on the page (no need to leave for /admin). When unlocked, it
 * renders the children with isAdmin=true. The password is kept in memory
 * (and localStorage) so the admin stays unlocked while browsing.
 *
 * Usage:
 *   <InlineAdminGate label="Gerer KAZAJAH TV">
 *     {({ isAdmin, adminPwd, lock }) => isAdmin && <YourEditUI ... />}
 *   </InlineAdminGate>
 */
export default function InlineAdminGate({ label = 'Admin', children, accent = 'yellow' }) {
  const [pwd, setPwd] = useState('');
  const [adminPwd, setAdminPwd] = useState(() => {
    try { return localStorage.getItem(ADMIN_PASSWORD_LOCAL_STORAGE_KEY) || ''; } catch { return ''; }
  });
  const [showLogin, setShowLogin] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = !!adminPwd;

  const accentColors = {
    yellow: { bg: 'bg-yellow-500', hover: 'hover:bg-yellow-400', ring: 'focus:border-yellow-500', text: 'text-yellow-300', border: 'border-yellow-500/30' },
    pink:   { bg: 'bg-pink-500',   hover: 'hover:bg-pink-400',   ring: 'focus:border-pink-500',   text: 'text-pink-300',   border: 'border-pink-500/30' },
    red:    { bg: 'bg-red-500',    hover: 'hover:bg-red-400',    ring: 'focus:border-red-500',    text: 'text-red-300',    border: 'border-red-500/30' },
  };
  const c = accentColors[accent] || accentColors.yellow;

  const tryLogin = async () => {
    setError('');
    // The backend has no dedicated "verify" endpoint, but verify_admin is enforced on every
    // protected call. We do a soft-check by hitting an admin-protected endpoint.
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/community/all?password=${encodeURIComponent(pwd)}`);
      if (r.status === 401 || r.status === 403) {
        setError('Mot de passe incorrect');
        return;
      }
      if (!r.ok) {
        setError(`Erreur ${r.status}`);
        return;
      }
      setAdminPwd(pwd);
      try { localStorage.setItem(ADMIN_PASSWORD_LOCAL_STORAGE_KEY, pwd); } catch {}
      setPwd('');
      setShowLogin(false);
    } catch (e) {
      setError('Erreur reseau');
    }
  };

  const lock = () => {
    setAdminPwd('');
    try { localStorage.removeItem(ADMIN_PASSWORD_LOCAL_STORAGE_KEY); } catch {}
  };

  return (
    <>
      {/* Floating admin button (bottom-right) */}
      <div className="fixed bottom-20 right-4 z-50" data-testid="inline-admin-gate">
        {!isAdmin && !showLogin && (
          <button onClick={() => setShowLogin(true)}
            data-testid="inline-admin-open"
            className={`px-3 py-2 ${c.bg} ${c.hover} text-black font-bold rounded-full shadow-lg inline-flex items-center gap-1.5 text-xs`}
            title="Connexion admin">
            <Lock size={12} /> {label}
          </button>
        )}
        {!isAdmin && showLogin && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className={`bg-zinc-950 border ${c.border} rounded-2xl p-4 shadow-2xl w-72`}>
            <div className="flex items-center justify-between mb-2">
              <p className={`text-xs font-bold uppercase tracking-wider ${c.text}`}>Connexion admin</p>
              <button onClick={() => { setShowLogin(false); setError(''); }}
                className="text-zinc-500 hover:text-white"><X size={14} /></button>
            </div>
            <input type="password" value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && tryLogin()}
              placeholder="Mot de passe"
              autoFocus
              data-testid="inline-admin-password"
              className={`w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-white text-sm focus:outline-none ${c.ring}`} />
            {error && <p className="mt-1.5 text-[10px] text-red-400">{error}</p>}
            <button onClick={tryLogin}
              data-testid="inline-admin-submit"
              className={`mt-2 w-full py-2 ${c.bg} ${c.hover} text-black font-bold rounded-lg text-xs`}>
              Connexion
            </button>
          </motion.div>
        )}
        {isAdmin && (
          <button onClick={lock}
            data-testid="inline-admin-lock"
            className={`px-3 py-2 bg-zinc-900 border ${c.border} ${c.text} font-bold rounded-full shadow-lg inline-flex items-center gap-1.5 text-xs hover:bg-zinc-800`}
            title="Verrouiller (deconnexion admin)">
            <Unlock size={12} /> Admin actif
          </button>
        )}
      </div>

      {/* Render-prop pattern */}
      <AnimatePresence>
        {typeof children === 'function' && children({ isAdmin, adminPwd, lock })}
      </AnimatePresence>
    </>
  );
}
